import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogTitle, DialogContent, TextField, Button, DialogActions, Typography, Box, IconButton, Select, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Backdrop, CircularProgress } from '@mui/material';
import { normalizeCustomerName, uppercaseCustomerNameInput } from '../../../utils/customerInput';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

const tiposDocumentos = ["CC", "NIT", "TI", "PPT"]

const API_URL = process.env.REACT_APP_API_URL;

export default function DialogCreateCliente({ openDialog, handleCloseDialog, handleUpdateData }) {
  const [nombre, setNombre] = useState("");
  const [apellidos, setApellidos] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [documento, setDocumento] = useState("");
  const [celular, setCelular] = useState("");
  const [loading, setLoading] = useState(false);
  const submitRef = useRef(false);

  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    nombre: false,
    apellidos: false,
    tipoDocumento: false,
    documento: false,
    celular: false,
  });

  const validateFields = () => {
    const errors = {
      nombre: nombre.trim() === "",
      apellidos: apellidos.trim() === "",
      tipoDocumento: tipoDocumento.trim() === "",
      documento: documento.trim() === "",
      celular: celular.trim() === "",
    };
    setFieldErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = async () => {
    if (submitRef.current || loading) return;
    if (!validateFields()) {
      setError("Por favor, complete todos los campos.");
      return;
    }
    submitRef.current = true;
    setLoading(true);

    const updatedData = {
      nombre: normalizeCustomerName(nombre),
      apellidos: normalizeCustomerName(apellidos),
      tipo_documento: tipoDocumento,
      documento,
      celular,
    };

    try {
      const response = await axios.post(`${API_URL}/clientes/`, updatedData);
      if (response.status === 201) {
        handleUpdateData();
        handleCloseDialog();
      } else {
        throw new Error("Error al crear el cliente");
      }
    } catch (err) {
      setError(getApiErrorMessage(
        err,
        'No fue posible crear el cliente.',
        ['non_field_errors', 'documento', 'nombre', 'apellidos']
      ));
    } finally {
      submitRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog open={openDialog} onClose={handleCloseDialog}>
      <Backdrop
        open={loading}
        style={{ zIndex: 9999, color: '#fff' }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <DialogTitle>Crear Cliente</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Nombre"
          fullWidth
          value={nombre}
          error={fieldErrors.nombre}
          helperText={fieldErrors.nombre ? "Este campo es obligatorio" : ""}
          onChange={(e) => setNombre(uppercaseCustomerNameInput(e.target.value))}
          onBlur={() => setNombre(normalizeCustomerName(nombre))}
        />
        <TextField
          autoFocus
          margin="dense"
          label="Apellidos"
          fullWidth
          value={apellidos}
          error={fieldErrors.apellidos}
          helperText={fieldErrors.apellidos ? "Este campo es obligatorio" : ""}
          onChange={(e) => setApellidos(uppercaseCustomerNameInput(e.target.value))}
          onBlur={() => setApellidos(normalizeCustomerName(apellidos))}
        />
        <Typography variant="subtitle2" sx={{ marginTop: 2 }}>Tipo Documento</Typography>
        <Select
          margin="dense"
          fullWidth
          value={tipoDocumento}
          error={fieldErrors.tipoDocumento}
          onChange={(e) => setTipoDocumento(e.target.value)}
        >
          {tiposDocumentos.map((tipo) => (
            <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
          ))}
        </Select>
        <TextField
          margin="dense"
          label="Documento"
          fullWidth
          value={documento}
          error={fieldErrors.documento}
          helperText={fieldErrors.documento ? "Este campo es obligatorio" : ""}
          onChange={(e) => setDocumento(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Celular"
          fullWidth
          value={celular}
          error={fieldErrors.celular}
          helperText={fieldErrors.celular ? "Este campo es obligatorio" : ""}
          onChange={(e) => setCelular(e.target.value)}
        />
      </DialogContent>
      {error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#d32f2f', padding: 1, borderRadius: 1, width: '95%' }}>
            <Typography sx={{ color: 'white', fontWeight: 'bold', marginRight: 1 }}>{error}</Typography>
            <IconButton color="inherit" onClick={() => setError("")}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}
      <DialogActions>
        <Button onClick={handleCloseDialog} variant="outlined" color='error'>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
